import React, { useEffect, useRef, useState } from 'react';
import { Post as PostType } from '../../../types';
import ImgProfilePost from '../ImgProfilePost';
import UserName from '../UserName';
import "./style.css"
import ImgPost from '../ImgPost/ImgPost';
import PinPost from '../Config/Pin';
import ShareProfile from '../Config/ShareProfile/ShareProfile';
import { ContainerButtonAds } from '../../container-button-ads/container-button-ads';
import BlockUser from '../Config/BlockUser';
import Report from '../Config/Report';
import DropdownPost from '../dropdown-post';
import { YoutubePreview } from '../Post/youtube-preview';
import { PostLinkPreview } from '../Post/post-link-preview';
import { censorText, clearText, extractFirstUrl, extractYouTubeId } from '../../../global-components/src/utils/text';
import { renderTextWithPhoneMessageLinks } from '../../../utils/phone-message-links';
import { Frame } from '../ImgProfilePost/frame';
import { useNavigate } from 'react-router-dom';
import PostPoll from '../Post/poll';
import { detectLanguage, isUserLang, translate } from '../../../global-components/src/utils/language';
import axios from 'axios';
import { useTheme } from '../../../global-components/src';

interface PostProps {
    post: PostType; // Define que posts é um array de Post
    onReport: (removeReport: boolean, id_user: number, id_post: number) => void;
    onEdit: (post: PostType) => void;
    onDelete: (post: PostType) => void;
    setPosts?: React.Dispatch<React.SetStateAction<PostType[]>>;
    setPost?: React.Dispatch<React.SetStateAction<PostType | null>>;
    onOpenComments?: () => void;
}

const Repost: React.FC<PostProps> = ({ post, onReport, onEdit, onDelete, setPosts, setPost, onOpenComments }) => {
    const { hideCensorship } = useTheme();
    const renderPostText = (text?: string) => {
        if (!text) return "";
        return renderTextWithPhoneMessageLinks(
            !hideCensorship ? censorText(text) : text,
            clearText,
        );
    };
    const [processedText, setProcessedText] = useState<string>(renderPostText(post?.text));
    const youtubeId = extractYouTubeId(post?.text ?? "");
    const hasLink = extractFirstUrl(post?.text ?? "");
    const navigate = useNavigate();
    const hasYoutube = Boolean(youtubeId);
    const hasImages = post?.images?.length > 0;
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [canExpand, setCanExpand] = useState<boolean>(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const elementRef = useRef<HTMLDivElement>(null);
    const [oldProcessedText, setOldProcessedText] = useState<string>(renderPostText(post?.text));
    const [translatedText, setTranslatedText] = useState<string>("");
    const [isTranslate, setIsTranslate] = useState<boolean>(false);
    const [canTranslate, setCanTranslate] = useState<boolean>(false);
    const language = detectLanguage(post?.text ?? "");

    useEffect(() => {
        if(!isUserLang(language)) translateText();
    }, [post?.text]);

    const translateText = async(updateText = false) => {
        if(translatedText.length && updateText){
            setProcessedText(translatedText);
            setIsTranslate(true);
            return;
        }

        try {
            let translated = await translate(post.text);
            if(!translated || post.text?.trim() == translated.trim()) return;
            translated = renderPostText(translated)
            setTranslatedText(translated)
            
            if(updateText){
                translated = renderPostText(translated ?? "");
                setProcessedText(translated);
                setIsTranslate(true);
            }
            setCanTranslate(true);
        } catch (err: any) {
            console.error('Translation error:', err?.response?.data || err);
            return null;
        }
    }

    const showOriginal = async() => {
        setProcessedText(oldProcessedText);
        setIsTranslate(false)
    }

    useEffect(() => {
        if (textRef.current) {
            const realHeight = textRef.current.scrollHeight;
            const maxHeight = 100; // Mesmo valor de maxHeight do CSS
            // Verifica se o resultado final é apenas um emoji (img tag)
            const onlyEmoji = processedText.trim().match(/^<img[^>]*class=['"]emoji['"][^>]*>$/);
            setCanExpand(realHeight > (maxHeight + 100) && !onlyEmoji);
        }
    }, [post?.text]);

    return (
        <div key={`${post?.id}`} style={{ position: "relative" }}>
            <div className={`card ${post?.user?.active_frame?.image_path && !post.group_id ? "acard-repost-with-frame" : ""} card-repost ${hasImages ? "card-repost-with-media" : ""} ${post?.images.length || hasYoutube || post?.poll || hasLink ? "card-repost-without-image" : ""}`}>
                <div className="row" style={{ flexWrap: "nowrap", overflow: "hidden" }}>
                    <ImgProfilePost post={post}/>
                    <UserName post={post} />

                    <DropdownPost post={post} onDelete={onDelete} onEdit={onEdit} onReport={onReport}/>
                </div>

                {post?.text && !post.gif_url && (
                    <div ref={elementRef}
                        className={`row row-text-repost ${canExpand ? "cursor-pointer" : ""}`}
                        onClick={() => canExpand && setIsExpanded(prev => !prev)}
                    >
                        <p
                            ref={textRef}
                            className="text-repost post text-expand"
                            style={{
                                maxHeight: !isExpanded ? 200 : "none",
                                overflow: !isExpanded ? "hidden" : "visible",
                                transition: "max-height 0.25s ease"
                            }}
                            dangerouslySetInnerHTML={{ __html: processedText }}
                        />

                        {!isExpanded && canExpand ? (
                            <span className="point-post">
                                <span>Ler mais</span>
                            </span>
                        ) : (
                            <>
                                {!isUserLang(language) && canTranslate && !isTranslate && (
                                    <span className='user-select-none text-translate-post'>
                                        <span onClick={() => translateText(true)}>Traduzir</span>
                                    </span>
                                )}
                                {!isUserLang(language) && canTranslate && isTranslate && (
                                    <span className='user-select-none text-translate-post'>
                                        <span onClick={showOriginal}>Mostrar original</span>
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                )}

            </div>

            {post.gif_url && (
                <img
                    src={post.gif_url}
                    className="post-gif"
                    alt="GIF"
                />
            )}

            {/* YOUTUBE TEM PRIORIDADE */}
            {hasYoutube && !hasImages && !post.gif_url && (
                <YoutubePreview text={post.text} />
            )}

            {/* LINK PREVIEW SÓ SE NÃO FOR YOUTUBE */}
            {!hasYoutube && !hasImages && !post.gif_url && hasLink && (
                <PostLinkPreview
                    text={post.text}
                    preview={post.link_preview}
                    postId={post.id}
                    className="post-small-link-preview"
                />
            )}
            
            <ImgPost onComment={onOpenComments} setPosts={setPosts} setPost={setPost} onLike={() => {}} img_account={post?.user?.img_account} images={post?.images} post={post} is_repost={true}/>

            {post?.poll && (
                <PostPoll
                    poll={post.poll}
                    postId={post.id}
                />
            )}
            
            {/* {post.user?.active_frame?.image_path && !post.group_id &&
                <Frame onClick={() => navigate(`/@${post?.user.user_name}`) } path={`${BUCKET_PACOCA_URL}/${post.user.active_frame?.image_path}`} className="cursor-pointer repost"/>
            } */}
            <ContainerButtonAds post={post}/>
        </div>
    );
};

export default Repost;
